import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { ppeAPI, departmentAPI, isRequestCanceled } from '../utils/api';
import { useListRequest } from '../hooks/useListRequest';
import type { PPESummaryData, PPEMetricBlock } from '../types/ppe';
import type { Department } from '../types/department';
import { getTodayDateInCompanyTimezone } from '../utils/timezone';

import ScopeIndicator from '../components/ScopeIndicator';

const PPESummary: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canViewPPESummary } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'ppeSummary' });
  const { t: tc } = useTranslation('common');
  const [summaryData, setSummaryData] = useState<PPESummaryData | null>(null);
  const [ppeTypes, setPpeTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPPEDisabled, setIsPPEDisabled] = useState(false);

  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    department_id: ''
  });

  const [formState, setFormState] = useState({
    date_from: '',
    date_to: '',
    department_id: ''
  });

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const currentCompanyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const { begin, cancel } = useListRequest(JSON.stringify([currentCompany?.company_id, filters]));

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadSummary = useCallback(async () => {
    if (!currentCompany || !canViewPPESummary) return;
    const { signal } = begin();
    if (signal.aborted) return;

    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;

    try {
      if (!isMountedRef.current) return;

      setLoading(true);
      setError(null);
      setIsPPEDisabled(false);
      setSummaryData(null);

      const response = await ppeAPI.getPPESummary(currentCompany.company_id, filters, signal);
      
      if (!signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setSummaryData(response.data || null);
        setPpeTypes(response.ppe_types || []);
      }
    } catch (err: any) {
      if (signal.aborted || isRequestCanceled(err)) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        console.error('Error loading PPE summary:', err);
        if (err.response?.status === 403) {
          setIsPPEDisabled(true);
          setError(null);
        } else {
          setError(err.response?.data?.message || err.message || t('loadFailed'));
        }
      }
    } finally {
      if (!signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setLoading(false);
      }
    }
  }, [currentCompany, filters, canViewPPESummary, t, begin]);

  const loadDepartments = useCallback(async () => {
    if (!currentCompany?.company_id) return;

    try {
      setLoadingDepartments(true);
      const response = await departmentAPI.getDepartments(currentCompany.company_id);
      setDepartments(response.departments || []);
    } catch (err) {
      console.error('Error loading departments:', err);
    } finally {
      setLoadingDepartments(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    if (currentCompany) {
      const today = getTodayDateInCompanyTimezone(currentCompany.timezone || 'UTC');
      const initialFilters = {
        date_from: today,
        date_to: today,
        department_id: ''
      };
      setFilters(initialFilters);
      setFormState(initialFilters);
      setIsPPEDisabled(false);
      setSummaryData(null);
      setError(null);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadSummary();
    return cancel;
  }, [loadSummary, cancel]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <div className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canViewPPESummary) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <Alert variant="danger">{t('noAccess')}</Alert>
        </Container>
      </Layout>
    );
  }

  const handleFilterChange = (field: string, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();

    if (formState.date_from && formState.date_to && formState.date_from > formState.date_to) {
      setError(t('dateRangeError'));
      return;
    }

    setError(null);
    setFilters({ ...formState });
  };

  const clearFilters = () => {
    const timezone = currentCompany?.timezone || 'UTC';
    const today = getTodayDateInCompanyTimezone(timezone);
    const clearedForm = {
      date_from: today,
      date_to: today,
      department_id: ''
    };
    setFormState(clearedForm);
    setFilters(clearedForm);
    setError(null);
  };

  const KPICard = ({ title, value, subtitle, icon, color }: { title: string, value: string | number, subtitle?: string, icon: React.ReactNode, color: string }) => (
    <Card className="h-100 border-0 shadow-sm an-data-card an-token-card-radius">
      <Card.Body className="d-flex align-items-center an-data-card">
        <div className={`p-3 me-3 an-metric-icon bg-${color} bg-opacity-10 text-${color}`} style={{ fontSize: '1.5rem' }}>
          {icon}
        </div>
        <div>
          <h6 className="text-muted mb-1">{title}</h6>
          <h3 className="fw-bold mb-0">{value}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
      </Card.Body>
    </Card>
  );

  const MetricBlock = ({ title, metric }: { title: string, metric: PPEMetricBlock }) => {
    const rate = metric.compliance_rate;
    const variant = rate >= 90 ? 'success' : rate >= 75 ? 'warning' : 'danger';
    const totalDetections = (metric.compliant ?? 0) + (metric.non_compliant ?? 0);

    return (
      <Col md={6} lg={4} className="mb-4">
        <Card className="h-100 border-0 shadow-sm an-data-card an-token-card-radius">
          <Card.Header className="bg-transparent border-0 pt-4 px-4 an-data-card">
            <h5 className="fw-bold mb-0">{title}</h5>
          </Card.Header>
          <Card.Body className="px-4 pb-4 an-data-card">
            <div className="d-flex justify-content-between align-items-end mb-2">
              <div>
                <span className="display-6 fw-bold text-primary">{rate.toFixed(1)}%</span>
                <div className="text-muted small">{t('complianceRate')}</div>
              </div>
              <div className="text-end">
                <div className="fw-bold text-success">{metric.compliant} {t('compliant')}</div>
                <div className="text-danger small">{metric.non_compliant} {t('nonCompliant')}</div>
              </div>
            </div>
            <ProgressBar now={rate} variant={variant} style={{ height: '8px' }} className="mb-3 an-token-radius-sm" />
            <div className="text-muted small">{t('totalDetections')}: {totalDetections}</div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  if (isPPEDisabled) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <Alert variant="info" className="mb-4">
            <Alert.Heading>{t('ppeDisabledTitle')}</Alert.Heading>
            <p>{t('ppeDisabledMessage')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
        <div className="page-header">
          <h2 className="an-page-title">{t('title')}</h2>
        </div>

        <ScopeIndicator resource="ppe" />

        <Card className="mb-4 border-0 shadow-sm an-data-card an-token-card-radius an-ppe-filter-card">
          <Card.Body className="an-data-card">
            <Form onSubmit={applyFilters}>
              <Row className="align-items-end">
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted">{tc('labels.fromDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formState.date_from}
                      onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted">{tc('labels.toDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formState.date_to}
                      onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted">{tc('labels.department')}</Form.Label>
                    <Form.Select
                      value={formState.department_id}
                      onChange={(e) => handleFilterChange('department_id', e.target.value)}
                      disabled={loadingDepartments}
                    >
                      <option value="">{tc('labels.allDepartments')}</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" className="flex-grow-1" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : tc('buttons.apply')}
                    </Button>
                    <Button variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                      {tc('buttons.clear')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && summaryData && (
          <>
            <Row className="mb-4">
              <Col md={4} className="mb-3 mb-md-0">
                <KPICard
                  title={t('totalDetections')}
                  value={summaryData.total_detections ?? 0}
                  icon={<i className="bi bi-search"></i>}
                  color="primary"
                  subtitle={t('detectionsInRange')}
                />
              </Col>
              <Col md={4} className="mb-3 mb-md-0">
                <KPICard
                  title={t('fullyCompliant')}
                  value={summaryData.full_compliance?.count ?? 0}
                  subtitle={t('ofAllDetections', { percentage: summaryData.full_compliance?.percentage?.toFixed(1) ?? '0.0' })}
                  icon={<i className="bi bi-check-lg"></i>}
                  color="success"
                />
              </Col>
              <Col md={4}>
                <KPICard
                  title={t('dateRange')}
                  value={`${summaryData.date_range?.from ?? tc('messages.na')}`}
                  subtitle={`${t('to')} ${summaryData.date_range?.to ?? tc('messages.na')}`}
                  icon={<i className="bi bi-calendar3"></i>}
                  color="info"
                />
              </Col>
            </Row>

            <h4 className="fw-bold mb-4 mt-5">{t('metricsByPpeType')}</h4>
            <Row>
              {ppeTypes.includes('hairnet_gloves') && summaryData.hairnet && (
                <MetricBlock title={t('hairnet')} metric={summaryData.hairnet} />
              )}
              {ppeTypes.includes('hairnet_gloves') && summaryData.left_glove && (
                <MetricBlock title={t('leftGlove')} metric={summaryData.left_glove} />
              )}
              {ppeTypes.includes('hairnet_gloves') && summaryData.right_glove && (
                <MetricBlock title={t('rightGlove')} metric={summaryData.right_glove} />
              )}
              {ppeTypes.includes('helmet') && summaryData.helmet && (
                <MetricBlock title={t('helmet')} metric={summaryData.helmet} />
              )}
              {ppeTypes.includes('industrial_gloves') && summaryData.industrial_gloves && (
                <MetricBlock title={t('industrialGloves')} metric={summaryData.industrial_gloves} />
              )}
              {ppeTypes.includes('industrial_jacket') && summaryData.industrial_jacket && (
                <MetricBlock title={t('industrialJacket')} metric={summaryData.industrial_jacket} />
              )}
            </Row>
          </>
        )}

        {!loading && !summaryData && !error && (
          <div className="text-center py-5 an-data-empty-state">
            <div style={{ fontSize: '3rem' }}><i className="bi bi-bar-chart-line"></i></div>
            <h5 className="text-muted mt-3">{t('noSummaryData')}</h5>
            <p className="text-muted">{t('adjustFilters')}</p>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default PPESummary;

